import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Client, IMessage } from '@stomp/stompjs';
import { BehaviorSubject, Subject } from 'rxjs';
import { Message } from '../models/message.model';
import { environment } from '../../environments/environment';

const isBrowser = typeof WebSocket !== 'undefined';

@Injectable({ providedIn: 'root' })
export class StaffChatService implements OnDestroy {
  private stompClient: Client | null = null;

  private messagesSubject = new Subject<Message>();
  public messages$ = this.messagesSubject.asObservable();

  private unreadCountSubject = new BehaviorSubject<number>(0);
  public unreadCount$ = this.unreadCountSubject.asObservable();

  private notificationSubject = new Subject<Message>();
  public notification$ = this.notificationSubject.asObservable();

  private typingEventsSubject = new Subject<import('../models/typing-event.model').TypingEvent>();
  public typingEvents$ = this.typingEventsSubject.asObservable();

  private activeConversationId: string | null = null;
  private connected = false;

  constructor(private zone: NgZone) {}

  connect(): void {
    if (!isBrowser || this.connected) return;

    const token = sessionStorage.getItem('access_token');
    if (!token) return;

    this.requestNotificationPermission();

    const wsUrl =
      environment.APP_BASE_URL.replace('https://', 'wss://').replace('http://', 'ws://') +
      `/ws?token=${encodeURIComponent(token)}`;

    this.stompClient = new Client({
      webSocketFactory: () => new WebSocket(wsUrl),
      reconnectDelay: 5000,
      onConnect: () => {
        this.connected = true;
        this.subscribeToMessages();
        this.subscribeToTypingEvents();
      },
      onDisconnect: () => {
        this.connected = false;
      },
      onStompError: (frame) => {
        console.error('STOMP error', frame);
      },
    });

    this.stompClient.activate();
  }

  disconnect(): void {
    this.stompClient?.deactivate();
    this.connected = false;
  }

  sendMessage(message: Message): void {
    if (!this.connected || !this.stompClient?.connected) return;
    this.stompClient.publish({
      destination: '/app/staff-chat.send',
      body: JSON.stringify(message),
    });
  }

  sendTypingEvent(receiverId: string, conversationId: string, senderName: string): void {
    if (!this.connected || !this.stompClient?.connected) return;
    
    let senderId = '';
    try {
      const profile = localStorage.getItem('userProfile');
      if (profile) {
        senderId = JSON.parse(profile).id;
      }
    } catch {
      // ignore
    }
    
    this.stompClient.publish({
      destination: '/app/staff-chat.typing',
      body: JSON.stringify({
        conversationId,
        senderId,
        receiverId,
        senderName,
      }),
    });
  }

  setActiveConversation(conversationId: string | null): void {
    this.activeConversationId = conversationId;
    if (conversationId) {
      this.unreadCountSubject.next(0);
    }
  }

  private subscribeToMessages(): void {
    this.stompClient?.subscribe('/user/queue/staff-messages', (frame: IMessage) => {
      this.zone.run(() => {
        try {
          const msg: Message = JSON.parse(frame.body);
          this.messagesSubject.next(msg);

          // Only raise a notification if the message is incoming and we're not in that conversation
          const isFromMe = this.isSentByMe(msg);
          if (!isFromMe && msg.conversationId !== this.activeConversationId) {
            this.unreadCountSubject.next(this.unreadCountSubject.value + 1);
            this.notificationSubject.next(msg);
            this.playNotificationTone();
            this.showBrowserNotification(msg);
          }
        } catch (e) {
          console.error('Failed to parse STOMP message', e);
        }
      });
    });
  }

  private subscribeToTypingEvents(): void {
    this.stompClient?.subscribe('/user/queue/staff-typing', (frame: IMessage) => {
      this.zone.run(() => {
        try {
          const event: import('../models/typing-event.model').TypingEvent = JSON.parse(frame.body);
          this.typingEventsSubject.next(event);
        } catch (e) {
          console.error('Failed to parse STOMP typing event', e);
        }
      });
    });
  }

  private playNotificationTone(): void {
    try {
      const AudioCtx = window.AudioContext ?? (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx() as AudioContext;

      const play = (freq: number, startAt: number, duration: number) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.25, ctx.currentTime + startAt);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + startAt + duration);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(ctx.currentTime + startAt);
        osc.stop(ctx.currentTime + startAt + duration);
      };

      play(880, 0, 0.18);
      play(1100, 0.2, 0.22);

      setTimeout(() => ctx.close(), 600);
    } catch {
      /* audio blocked or unsupported */
    }
  }

  private requestNotificationPermission(): void {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }

  private showBrowserNotification(msg: Message): void {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    if (document.visibilityState === 'visible') return; // already in the tab

    const notif = new Notification(msg.senderName ?? 'Team Chat', {
      body: msg.content?.substring(0, 100) ?? '',
      icon: '/logo_no_background.png',
      tag: 'staff-chat', // replaces previous notification instead of stacking
    });

    notif.onclick = () => {
      window.focus();
      notif.close();
    };
  }

  private isSentByMe(msg: Message): boolean {
    try {
      const profile = localStorage.getItem('userProfile');
      if (!profile) return false;
      const user = JSON.parse(profile);
      return user.id === msg.senderId;
    } catch {
      return false;
    }
  }

  ngOnDestroy(): void {
    this.disconnect();
  }
}
